import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, User, ArrowRight, CheckCircle, ArrowLeft, X, LogOut } from 'lucide-react';
import axios from 'axios';
import { API, clearAuthData } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_imageblaze/artifacts/5e5w021j_7D777DBC-744F-44B3-9C70-7AE1E6146D31.png";

const CompleteRegistration = ({ user }) => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Redirect if user already has completed their profile
  useEffect(() => {
    if (user?.first_name && user?.last_name && user?.phone) {
      // User already has all required info, redirect to appropriate page
      if (user.status === 'approved') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/pending-approval', { replace: true });
      }
    }
  }, [user, navigate]);

  // Handle cancel/quit registration
  const handleCancelRegistration = async () => {
    try {
      // Logout the user
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear all auth data
    clearAuthData();
    // Redirect to landing page
    navigate('/', { replace: true });
  };

  // Handle go back
  const handleGoBack = () => {
    setShowCancelConfirm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !phone) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    // Validate phone format
    const phoneRegex = /^(\+33|0)[1-9](\d{2}){4}$/;
    if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
      setError('Format de téléphone invalide (ex: 06 12 34 56 78)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${API}/auth/complete-registration`,
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone.replace(/\s/g, '')
        },
        { withCredentials: true }
      );

      navigate('/pending-approval', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header avec bouton retour */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm">Retour</span>
            </button>
            <button
              onClick={() => setShowCancelConfirm(true)}
              className="flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
              data-testid="cancel-btn"
              title="Annuler l'inscription"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Logo DLV Express */}
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="DLV EXPRESS" className="h-20 w-auto mx-auto" />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">Complétez votre inscription</h1>
            <p className="text-gray-500 mt-2">
              Bienvenue ! Veuillez fournir les informations requises.
            </p>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm text-gray-600">Connexion</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0F172A] rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">2</span>
              </div>
              <span className="text-sm font-medium text-[#0F172A]">Informations</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-gray-500 text-sm">3</span>
              </div>
              <span className="text-sm text-gray-400">Approbation</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="firstName" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Prénom <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Votre prénom"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="firstname-input"
                className="h-12"
                required
              />
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="lastName" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Nom de famille <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Votre nom de famille"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                data-testid="lastname-input"
                className="h-12"
                required
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Numéro de téléphone <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="06 12 34 56 78"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="phone-input"
                className="h-12"
                required
              />
              <p className="text-xs text-gray-500">Ce numéro sera utilisé pour vous contacter</p>
            </div>

            <Button
              type="submit"
              disabled={loading || !firstName || !lastName || !phone}
              data-testid="submit-registration-btn"
              className="w-full h-12 btn-primary"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  Soumettre ma demande
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            {/* Lien annuler en bas */}
            <div className="text-center pt-4 border-t border-gray-100 mt-6">
              <button
                type="button"
                onClick={() => setShowCancelConfirm(true)}
                className="text-sm text-gray-500 hover:text-red-500 transition-colors flex items-center gap-2 mx-auto"
              >
                <LogOut className="w-4 h-4" />
                Annuler et quitter l'inscription
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de confirmation d'annulation */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogOut className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Quitter l'inscription ?</h3>
              <p className="text-gray-500 mt-2">
                Vous êtes sur le point de quitter le processus d'inscription. 
                Vos informations ne seront pas enregistrées.
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => setShowCancelConfirm(false)}
                className="w-full h-12 bg-[#0F172A] hover:bg-[#1e293b]"
              >
                Continuer l'inscription
              </Button>
              <Button
                onClick={handleCancelRegistration}
                variant="outline"
                className="w-full h-12 border-red-300 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Quitter l'inscription
              </Button>
            </div>

            <p className="text-xs text-gray-400 text-center mt-4">
              Vous pourrez vous réinscrire plus tard si vous le souhaitez.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompleteRegistration;
